import {
  Box,
  Chip,
  Typography,
  Zoom,
  Tooltip,
} from '@mui/material';
import { sendGTMEvent } from '@next/third-parties/google';
import { usePathname } from 'next/navigation';
import PublicIcon from '@mui/icons-material/Public';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import CopyAllIcon from '@mui/icons-material/CopyAll';

import { colorVariables } from '@/constants';
import { Vendor } from '@/lib/types/interfaces/Vendor';
import useCopyContacts from '@/hooks/useCopyContacts';
import { removeUrlProtocol } from '@/lib/utils/functions';

const OverviewSection = ({ service }: { service: Vendor }) => {
  const pathname = usePathname();

  const {
    contacts,
    handleEmail,
    handleCopyNumber,
    handlePhoneNumber,
  } = useCopyContacts({ service });

  const itemSx = {
    color: colorVariables.primaryColor,
    height: '100%',
    display: 'flex',
  };

  const handleCopyEmail = () => {
    sendGTMEvent({
      url: pathname,
      event: 'click_on_service_provider_email',
      service_provider_name: service.business_name,
    });
  };

  const renderSubcategoryChips = [...service.categories, ...service.subcategories]?.map((item) => (
    <Chip
      label={item.name}
      key={item.id}
      component='li'
    />
  ));

  const handleClickOnWebsite = () => {
    sendGTMEvent({
      url: pathname,
      event: 'click_on_service_provider_website',
      service_provider_name: service.business_name,
    });
  };

  const servicesProvided = service?.service_provided?.split('; ') || [];

  return (
    <>
      <Box display={{ xs: 'block', lg: 'none' }}>
        <Typography
          variant='h6'
          component='h2'
          fontWeight={600}
          color={colorVariables.textDescription}
        >
          Category
        </Typography>
        <Box
          display='flex'
          flexWrap='wrap'
          gap={1}
          marginTop={1}
          component='ul'
        >
          {renderSubcategoryChips}
        </Box>
      </Box>
      <Box marginTop={{ xs: 3, lg: 0 }}>
        <Typography
          variant='h6'
          component='h2'
          fontWeight={600}
          color={colorVariables.textDescription}
        >
          Description
        </Typography>
        <Typography
          variant='body1'
          fontWeight={400}
          component='div'
          color={colorVariables.textDescription}
        >
          <div dangerouslySetInnerHTML={{ __html: service.description_long }} />
        </Typography>
      </Box>

      {!!service?.service_provided.length && (
        <Box sx={{ marginTop: 3 }}>
          <Typography
            variant="h6"
            component="h2"
            fontWeight={600}
            color={colorVariables.textDescription}
          >
            Services Provided
          </Typography>
          <>
            {service.is_user_generated ? (
              <Typography
                variant="body1"
                fontWeight={400}
                component="div"
                color={colorVariables.textDescription}
              >
                <div
                  dangerouslySetInnerHTML={{ __html: service.service_provided }}
                />
              </Typography>
            ) : (
              <Box
                component="div"
                display="flex"
                flexDirection={{ xs: "column", lg: "row" }}
                fontWeight={400}
                color={colorVariables.textDescription}
                fontSize={16}
                gap={{ sx: 0, lg: "42px" }}
                lineHeight="150%"
                letterSpacing="0.15px"
              >
                <Box
                  component="ul"
                  sx={{ padding: 0, margin: 0 }}
                  paddingRight={{ sx: 0, lg: "90px" }}
                >
                  {servicesProvided
                    .slice(0, Math.ceil(servicesProvided.length / 2))
                    .map((item) => (
                      <li
                        key={item}
                        style={{
                          listStyleType: "disc",
                          marginLeft: "24px",
                          marginBottom: 4,
                        }}
                      >
                        {" "}
                        {item}
                      </li>
                    ))}
                </Box>
                <Box component="ul" sx={{ padding: 0, margin: 0 }}>
                  {servicesProvided
                    .slice(Math.ceil(servicesProvided.length / 2))
                    .map((item) => (
                      <li
                        key={item}
                        style={{
                          listStyleType: "disc",
                          marginLeft: "24px",
                          marginBottom: 4,
                        }}
                      >
                        {" "}
                        {item}
                      </li>
                    ))}
                </Box>
              </Box>
            )}
          </>
        </Box>
      )}
      {service?.licenses && (
        <Box sx={{ marginTop: 3 }}>
          <Typography
            variant="h6"
            component="h2"
            fontWeight={600}
            color={colorVariables.textDescription}
          >
            Licenses
          </Typography>
          <Typography
            variant="body1"
            fontWeight={400}
            component="div"
            color={colorVariables.textDescription}
          >
            <div dangerouslySetInnerHTML={{ __html: service.licenses }} />
          </Typography>
        </Box>
      )}

      {service?.associations && (
        <Box sx={{ marginTop: 3 }}>
          <Typography
            variant='h6'
            component='h2'
            fontWeight={600}
            color={colorVariables.textDescription}
          >
            Associations
          </Typography>
          <Typography
            variant='body1'
            fontWeight={400}
            component='div'
            color={colorVariables.textDescription}
          >
            <div dangerouslySetInnerHTML={{ __html: service.associations }} />
          </Typography>
        </Box>
      )}

      {service?.certifications && (
        <Box sx={{ marginTop: 3 }}>
          <Typography
            variant='h6'
            component='h2'
            fontWeight={600}
            color={colorVariables.textDescription}
          >
            Certifications
          </Typography>
          <Typography
            variant='body1'
            fontWeight={400}
            component='div'
            color={colorVariables.textDescription}
          >
            <div dangerouslySetInnerHTML={{ __html: service.certifications }} />
          </Typography>
        </Box>
      )}

      {service?.insurance && (
        <Box sx={{ marginTop: 3 }}>
          <Typography
            variant='h6'
            component='h2'
            fontWeight={600}
            color={colorVariables.textDescription}
          >
            Insurance
          </Typography>
          <Typography
            variant='body1'
            fontWeight={400}
            component='div'
            color={colorVariables.textDescription}
          >
            <div dangerouslySetInnerHTML={{ __html: service.insurance }} />
          </Typography>
        </Box>
      )}

      {(service.phone_number || service.email) && (
        <Box
          sx={{ marginTop: 3 }}
          display={{ xs: 'block', lg: 'none' }}
        >
          <Typography
            variant='h6'
            component='h2'
            fontWeight={600}
            color={colorVariables.textDescription}
          >
            Contacts
          </Typography>
          <Box sx={{ ...itemSx, marginTop: 1 }}>
            <PhoneIcon sx={{ marginRight: 1 }} />
            {contacts.isPhoneNumber ? (
              <Zoom in={contacts.isPhoneNumber}>
                <Typography
                  variant='body1'
                  component='div'
                  sx={{ color: 'initial', display: 'flex' }}
                >
                  {service.phone_number}
                  <Tooltip
                    title='Copied!'
                    open={contacts.isShowTooltip}
                  >
                    <CopyAllIcon
                      sx={{ marginLeft: 1 }}
                      onClick={() => handleCopyNumber(service.phone_number)}
                    />
                  </Tooltip>
                </Typography>
              </Zoom>
            ) : (
              <Typography
                variant='body1'
                component='div'
                onClick={handlePhoneNumber}
                sx={{ cursor: 'pointer' }}
              >
                View Phone Number
              </Typography>
            )}
          </Box>

          {service.email && (
          <Box sx={{ ...itemSx, marginTop: 2 }}>
            <EmailIcon sx={{ marginRight: 1 }} />
            {contacts.isShowEmail ? (
              <Zoom in={contacts.isShowEmail}>
                <a
                  href={
                    `mailto:${service.email}?subject=Inquiry Regarding ${service.business_name} Information&body=Hi ${service.business_name} %0DI found your company through propjump.com and was looking to get more information on your services.`
                  }
                  target='_blank'
                  rel='noreferrer'
                  style={{ color: 'initial' }}
                  onClick={handleCopyEmail}
                >
                  {service.email}
                </a>
              </Zoom>
            ) : (
              <Typography
                variant='body1'
                component='div'
                onClick={handleEmail}
                sx={{ cursor: 'pointer' }}
              >
                View Email
              </Typography>
            )}
          </Box>
          )}
        </Box>
      )}

      {service?.website && (
        <Box sx={{ marginTop: 3 }}>
          <Typography
            variant='h6'
            component='h2'
            fontWeight={600}
            color={colorVariables.textDescription}
          >
            Website
          </Typography>
          <Typography
            variant='body1'
            component='div'
            marginTop={1}
            display='flex'
            fontWeight={400}
            color={colorVariables.primaryColor}
          >
            <PublicIcon
              color='success'
              sx={{ marginRight: 1 }}
            />
            <a
              href={service.website}
              target='_blank'
              rel='nofollow noreferrer'
              onClick={handleClickOnWebsite}
            >
              {removeUrlProtocol(service.website)}
            </a>
          </Typography>
        </Box>
      )}
    </>
  );
};

export default OverviewSection;
